import { reactive, computed } from 'vue';
import { load as yamlLoad, dump as yamlDump } from 'js-yaml';
import { findScreenById } from '../../lib/applyPatch';
import { applyTextEditToScreenYaml } from '../../edit-agent/lib/applyTextEdit';

export type PreviewEdit =
  | { type: 'patch'; screenYaml: string }
  | { type: 'text-edit'; oldText: string; newText: string };

export type Registration = {
  screenId: string;
  order: number;
  edit: PreviewEdit;
};

/**
 * Module-level reactive store of active previews keyed by chunkKey.
 * Multiple chunks may target the same screen — the store composes them in
 * `order` so the Monaco preview slot reflects the stacked result.
 */
export const registrations = reactive<Record<string, Registration>>({});

export function registerPreview(chunkKey: string, reg: Registration): void {
  registrations[chunkKey] = reg;
}

export function unregisterPreview(chunkKey: string): void {
  delete registrations[chunkKey];
}

export const activeScreenIds = computed<string[]>(() => {
  const ids = new Set<string>();
  for (const r of Object.values(registrations)) ids.add(r.screenId);
  return [...ids];
});

export function composePreviewForScreen(
  screenId: string,
  currentYaml: string
): string | null {
  const entries = Object.values(registrations)
    .filter(r => r.screenId === screenId)
    .sort((a, b) => a.order - b.order);
  if (entries.length === 0) return null;

  let courseObj: any;
  try {
    courseObj = yamlLoad(currentYaml);
  } catch {
    return null;
  }
  const originalScreen = findScreenById(courseObj, screenId);
  if (!originalScreen) return null;

  let screenYaml = yamlDump(originalScreen, { lineWidth: -1 });

  for (const r of entries) {
    if (r.edit.type === 'patch') {
      screenYaml = r.edit.screenYaml;
    } else {
      const result = applyTextEditToScreenYaml(
        screenYaml,
        r.edit.oldText,
        r.edit.newText
      );
      if (result.found) screenYaml = result.screenYaml;
    }
  }

  return screenYaml;
}
